import Phaser from 'phaser';
import BattleStateManager from '../game/BattleStateManager.js';
import { loadRewardsMusicManifest, getCurrentTheme, loadItemsData, buildItemsById } from '../Utils.js';
import sessionStateManager from '../game/SessionStateManager.js';

export class RewardsScene extends Phaser.Scene {
  constructor() {
    super({ key: 'RewardsScene' });
    this.rewardsScript = null;
    this.currentPlayerIndex = 0;
    this.playerSprites = [];
    this.isPlaying = false;
    this.timeline = null;
    this.rewardsMusicManifest = [];
    this.currentRewardsMusic = null;
    this.currentRewardsMusicKey = null;
    this.itemsById = null; // Map for looking up item definitions by ID
  }

  async init(data) {
    // Reset all state variables to ensure clean start
    this.rewardsScript = data?.rewardsScript || BattleStateManager.getRewardsScript() || this.generateMockRewardsScript();
    this.currentPlayerIndex = 0;
    this.playerSprites = [];
    this.isPlaying = false;
    this.timeline = null;
    this.statsContainer = null;
    this.xpContainer = null;
    this.eligiblePlayers = [];
    
    console.log('[RewardsScene] Initialized with rewards script:', this.rewardsScript);
    
    // Load rewards music from theme
    try {
      this.rewardsMusicManifest = await loadRewardsMusicManifest();
      console.log('🎵 Rewards music manifest loaded:', this.rewardsMusicManifest);
    } catch (error) {
      console.warn('Failed to load rewards music manifest:', error);
    }
    
    // Load items data for icon lookups
    try {
      const items = await loadItemsData();
      this.itemsById = buildItemsById(items);
      console.log('[RewardsScene] Loaded items data:', items.length, 'items for icon lookups');
    } catch (error) {
      console.warn('[RewardsScene] Failed to load items data:', error);
      this.itemsById = new Map();
    }
  }

  create() {
    const { width, height } = this.cameras.main;

    // Update state to 'rewards'
    sessionStateManager.updateState('rewards');

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x0F0F23).setDepth(0);

    // Title
    this.add.text(width / 2, 60, 'Battle Rewards', {
      fontSize: '48px',
      color: '#FFD700',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(10);

    // Create player lineup at top
    this.createPlayerLineup();

    // Start rewards music
    this.playRewardsMusic();

    // Start the rewards sequence
    this.startRewardsSequence();
  }

  createPlayerLineup() {
    const { width, height } = this.cameras.main;
    const players = this.rewardsScript.players;
    
    // Filter out any random heroes that might have slipped through (safety measure)
    this.eligiblePlayers = players.filter(player => !player.isRandom);
    // Only show top 10 players in the rewards scene
    this.eligiblePlayers = this.eligiblePlayers.slice(0, 10);
    let playerCount = this.eligiblePlayers.length;
    
    console.log(`🎁 RewardsScene: Showing top ${playerCount} eligible players (filtered from ${players.length} total)`);
    
    // Calculate more compact spacing for players
    const maxSpacing = 150; // Maximum space between players
    const totalWidth = Math.min(playerCount * maxSpacing, width - 200); // Don't exceed screen width
    const startX = (width - totalWidth) / 2; // Center the group
    const spacing = playerCount > 1 ? totalWidth / (playerCount - 1) : 0;
    
    // Fixed scale for player sprites (much smaller)
    const playerScale = 0.3; // Much smaller scale
    
    this.eligiblePlayers.forEach((player, index) => {
      const x = playerCount === 1 ? width / 2 : startX + (index * spacing);
      const y = 150;
      
      // Create player container
      const playerContainer = this.add.container(x, y).setDepth(5);
      
      // Player sprite - try multiple texture sources
      let playerSprite;
      let textureKey = null;
      
      // Try different texture key formats
      if (player.heroId && this.textures.exists(`player_${player.heroId}`)) {
        textureKey = `player_${player.heroId}`;
      } else if (player.imageUrl && this.textures.exists(player.imageUrl)) {
        textureKey = player.imageUrl;
      } else if (player.isRealHero && player.heroId) {
        // For real heroes without custom textures, use theme fallback
        const themeTextures = ['easy1', 'easy2', 'easy3', 'elite1', 'elite2', 'elite3', 'expert1', 'expert2', 'hard1', 'hard2', 'normal1', 'normal2'];
        const fallbackTexture = themeTextures[index % themeTextures.length];
        if (this.textures.exists(fallbackTexture)) {
          textureKey = fallbackTexture;
        }
      } else {
        // For random heroes, use theme textures
        const themeTextures = ['easy1', 'easy2', 'easy3', 'elite1', 'elite2', 'elite3', 'expert1', 'expert2', 'hard1', 'hard2', 'normal1', 'normal2'];
        const fallbackTexture = themeTextures[index % themeTextures.length];
        if (this.textures.exists(fallbackTexture)) {
          textureKey = fallbackTexture;
        }
      }
      
      if (textureKey) {
        playerSprite = this.add.image(0, 0, textureKey);
        playerSprite.setScale(playerScale);
      } else {
        // Final fallback - create a colored rectangle with player initial
        const initial = player.name.charAt(0).toUpperCase();
        playerSprite = this.add.rectangle(0, 0, 40, 40, 0x4A4A4A);
        const initialText = this.add.text(0, 0, initial, {
          fontSize: '20px',
          color: '#FFFFFF',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0.5);
        playerContainer.add(initialText);
      }
      
      playerContainer.add(playerSprite);
      
      // Player name (positioned below smaller sprite)
      const nameText = this.add.text(0, 80, player.name, {
        fontSize: '24px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      playerContainer.add(nameText);
      
      // Level badge (positioned below name)
      const levelText = this.add.text(0, 105, `Lv.${player.level}`, {
        fontSize: '20px',
        color: '#FFD700',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      playerContainer.add(levelText);
      
      // Initially dim all players
      playerContainer.setAlpha(0.4);
      
      // Store reference
      this.playerSprites.push({
        container: playerContainer,
        sprite: playerSprite,
        nameText: nameText,
        levelText: levelText,
        playerData: player
      });
    });
  }

  startRewardsSequence() {
    if (this.isPlaying) {
      console.log('[RewardsScene] Rewards sequence already playing, skipping...');
      return;
    }
    
    // Ensure we have eligible players
    if (!this.eligiblePlayers || this.eligiblePlayers.length === 0) {
      console.warn('[RewardsScene] No eligible players found, ending sequence');
      this.endRewardsSequence();
      return;
    }
    
    this.isPlaying = true;
    this.currentPlayerIndex = 0;
    
    console.log(`[RewardsScene] Starting rewards sequence for ${this.eligiblePlayers.length} players`);
    
    // Process all players simultaneously - show XP and loot for each player vertically
    this.processAllPlayers();
  }

  processAllPlayers() {
    // Process all players in parallel - each gets their own XP countup and loot stack
    this.eligiblePlayers.forEach((playerData, index) => {
      const playerSprite = this.playerSprites[index];
      
      if (!playerData || !playerSprite) {
        console.warn(`[RewardsScene] Skipping player at index ${index} - missing data`);
        return;
      }
      
      // Highlight all players (they're all active now)
      playerSprite.container.setAlpha(1.0);
      
      // Start XP countup for this player immediately
      this.animateXPEarnedForPlayer(playerData, playerSprite, index);
    });
    
    // After all animations complete, end the sequence
    // Estimate completion time: XP countup (~1000ms) + loot items (100ms * item count)
    const lootCounts = this.eligiblePlayers.map(p => (p.rewards?.lootItems?.length || 0));
    const maxLootCount = lootCounts.length > 0 ? Math.max(...lootCounts) : 0;
    const estimatedDuration = 2000 + (maxLootCount * 100) + 1000; // XP + loot + buffer
    
    this.time.delayedCall(estimatedDuration, () => {
      console.log('[RewardsScene] All players processed, ending sequence');
      this.endRewardsSequence();
    });
  }

  // Removed highlightPlayer - all players are shown at once now

  // Removed showPlayerStats - no longer needed as we show everything under each player

  animateXPEarnedForPlayer(playerData, playerSprite, playerIndex) {
    const rewards = playerData.rewards;
    const xpEarned = rewards.xpEarned || 0;
    
    // Get player container position
    const playerContainer = playerSprite.container;
    const playerX = playerContainer.x;
    const playerY = playerContainer.y;
    
    // Create XP counter positioned below the player sprite
    const xpText = this.add.text(playerX, playerY + 130, 'XP: 0', {
      fontSize: '20px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(10);
    
    // Count up animation
    let currentXP = 0;
    const increment = Math.max(1, Math.ceil(xpEarned / 60)); // 60 frames for count-up
    
    const countUpTimer = this.time.addEvent({
      delay: 16, // ~60fps
      callback: () => {
        currentXP = Math.min(currentXP + increment, xpEarned);
        xpText.setText(`XP: ${currentXP}`);
        
        if (currentXP >= xpEarned) {
          countUpTimer.destroy();
          this.onXPCountCompleteForPlayer(playerData, playerX, playerY, xpText, playerIndex);
        }
      },
      repeat: -1
    });
  }

  onXPCountCompleteForPlayer(playerData, playerX, playerY, xpText, playerIndex) {
    const rewards = playerData.rewards;
    
    // Pop-up effect for XP
    this.tweens.add({
      targets: xpText,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 200,
      ease: 'Back.easeOut',
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        // Check for level up
        if (rewards.leveledUp) {
          this.showLevelUpForPlayer(playerData, playerX, playerY, playerIndex, xpText);
        } else {
          // Start showing loot items stacked vertically
          this.showLootRewardsForPlayer(playerData, playerX, playerY, xpText);
        }
      }
    });
  }
  
  showLevelUpForPlayer(playerData, playerX, playerY, playerIndex, xpText) {
    // Level up text positioned near the player
    const levelUpText = this.add.text(playerX, playerY + 160, 'LEVEL UP!', {
      fontSize: '32px',
      color: '#FFD700',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(15);
    
    // Animate level up
    levelUpText.setScale(0);
    this.tweens.add({
      targets: levelUpText,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: levelUpText,
          scaleX: 1,
          scaleY: 1,
          duration: 200,
          ease: 'Power2'
        });
      }
    });
    
    // Update level text in player lineup
    const playerSprite = this.playerSprites[playerIndex];
    if (playerSprite && playerSprite.levelText) {
      playerSprite.levelText.setText(`Lv.${playerData.rewards.newLevel}`);
    }
    
    // Show loot after level up animation
    this.time.delayedCall(800, () => {
      levelUpText.destroy();
      this.showLootRewardsForPlayer(playerData, playerX, playerY, xpText);
    });
  }

  // Removed showLevelUpAnimation - replaced with showLevelUpForPlayer

  showLootRewardsForPlayer(playerData, playerX, playerY, xpText) {
    const rewards = playerData.rewards;
    const lootItems = rewards.lootItems || [];
    const specialFinds = rewards.specialFinds || [];
    
    if (lootItems.length === 0 && specialFinds.length === 0) {
      // No loot for this player
      return;
    }
    
    // Sort loot items: tokens first, then everything else
    const sortedLootItems = [...lootItems].sort((a, b) => {
      const aIsToken = a.rarity === 'token' || a.tokenSymbol;
      const bIsToken = b.rarity === 'token' || b.tokenSymbol;
      
      if (aIsToken && !bIsToken) return -1; // Token comes first
      if (!aIsToken && bIsToken) return 1;  // Non-token comes after
      return 0; // Keep original order for same type
    });
    
    // Calculate starting Y position (below XP text or level up text)
    // If leveled up, level up text is at playerY + 160, so start loot at playerY + 240
    // Otherwise, start at playerY + 180 (below XP at playerY + 130)
    let startY = rewards.leveledUp ? playerY + 240 : playerY + 180;
    
    // Show all loot items stacked vertically with 100ms delay between each
    sortedLootItems.forEach((item, index) => {
      const itemY = startY + (index * 95); // Stack items 80px apart vertically
      
      // Delay each item by 100ms
      this.time.delayedCall(100 * index, () => {
        this.showLootItemForPlayer(item, playerX, itemY);
      });
    });
    
    // Handle special finds after all loot items
    if (specialFinds.length > 0) {
      const specialY = startY + (sortedLootItems.length * 80);
      this.time.delayedCall(100 * sortedLootItems.length, () => {
        this.showSpecialFindForPlayer(specialFinds[0], playerX, specialY);
      });
    }
  }

  showLootItemForPlayer(item, x, y) {
    // Get icon key for the item
    let iconKey = item.iconKey || null;
    
    // If no iconKey in item data, try to look it up from itemsById using itemId
    if (!iconKey && item.itemId && this.itemsById) {
      const itemDef = this.itemsById.get(item.itemId);
      if (itemDef && itemDef.iconKey) {
        iconKey = itemDef.iconKey;
      }
    }
    
    // For tokens, try to use a generic coin icon (fallback)
    if ((item.rarity === 'token' || item.tokenSymbol) && !iconKey) {
      // Try common token icon keys
      const tokenIconKeys = ['token-coin', 'coin', 'token'];
      for (const key of tokenIconKeys) {
        if (this.textures.exists(key)) {
          iconKey = key;
          break;
        }
      }
    }
    
    // Create loot item display (smaller for vertical stacking)
    const itemBg = this.add.rectangle(x, y, 100, 60, this.getRarityColor(item.rarity));
    itemBg.setAlpha(0.01);
    itemBg.setStrokeStyle(2, 0xFFFFFF);
    itemBg.setDepth(10);
    
    // Check if this is a token
    const isToken = item.rarity === 'token' || item.tokenSymbol;
    
    // Add item icon if available
    let itemIcon = null;
    if (iconKey && this.textures.exists(iconKey)) {
      itemIcon = this.add.image(x, y + 5, iconKey);
      //Smaller scale for tokens
      if (isToken) {
        itemIcon.setScale(0.3);
      } else {
        itemIcon.setScale(0.6);
      }
      itemIcon.setOrigin(0.5);
      itemIcon.setDepth(11);
    } else if (iconKey) {
      // Icon key exists but texture not loaded - log for debugging
      console.warn('[RewardsScene] Icon texture not found for key:', iconKey, 'itemId:', item.itemId);
    }
    
    //remove the word "potion" from the item name
    let itemNameText = item?.itemName && item.itemName.replace('Potion', '').trim() || 'Item';
    if(item?.rarity === 'token') {
      itemNameText = `$${item.tokenSymbol}`;
    }
    // For tokens, position the amount text below the sprite (sprite is at y - 5, so text should be lower)
    // For non-tokens, keep the original position
    const itemNameY = isToken ? y + 45 : y + 40;
    const itemName = this.add.text(x, itemNameY, itemNameText, {
      fontSize: '18px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
      wordWrap: { width: 160 }
    }).setOrigin(0.5).setDepth(11);
    
    
    // For tokens, always show quantity (even if 1). For other items, only show if quantity > 1
    const shouldShowQuantity = isToken ? (item.quantity && item.quantity > 0) : (item.quantity > 1);
    if (shouldShowQuantity) {
      // For tokens, position quantity below the item name. For non-tokens, position at y + 45
      const quantityY = isToken ? y + 60 : y + 55;
      const quantity = this.add.text(x, quantityY, `x${item.quantity || 1}`, {
        fontSize: '16px',
        color: '#CCCCCC',
        fontFamily: 'Arial, sans-serif',
        stroke: '#000000',
        strokeThickness: 1
      }).setOrigin(0.5).setDepth(11);
    }
    
    // Animate item appearance
    itemBg.setScale(0);
    if (itemIcon) itemIcon.setScale(0);
    this.tweens.add({
      targets: itemBg,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut'
    });
    if (itemIcon) {
      this.tweens.add({
        targets: itemIcon,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: 300,
        ease: 'Back.easeOut'
      });
    }
  }
  
  showSpecialFindForPlayer(specialFind, x, y) {
    const specialText = this.add.text(x, y, specialFind.message || 'Special Find!', {
      fontSize: '24px',
      color: '#FF6B6B',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(20);
    
    // Animate special find
    specialText.setScale(0);
    this.tweens.add({
      targets: specialText,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: specialText,
          scaleX: 1,
          scaleY: 1,
          duration: 200,
          ease: 'Power2'
        });
      }
    });
  }

  // Removed showSpecialFind and moveToNextPlayer - no longer needed with parallel processing

  endRewardsSequence() {
    const { width, height } = this.cameras.main;
    
    // Show completion message
    const completionText = this.add.text(width / 2, height * 0.9, 'Rewards Complete!', {
      fontSize: '36px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(15);
    
    // Create countdown progress bar at the bottom
    this.createProgressBar(width, height);
    
    // Mark that countdown should start (will be set on first update call when time is ready, delayed by 1 second for fade-in)
    this.countdownShouldStart = true;
    this.countdownDuration = 10000; // 12 seconds until transition
    this.countdownFadeInDelay = 1000; // 1 second delay for fade-in
    
    // Save the rewards script to database
    this.saveRewardsScript();
    
    //fade out the victory music
    if (this.currentVictoryMusic && this.currentVictoryMusicKey) {
      this.tweens.add({
        targets: this.currentVictoryMusic,
        volume: 0,
        duration: 5000,
        ease: 'Power2',
        onComplete: () => {
          if (this.currentVictoryMusic) {
            this.currentVictoryMusic.stop();
          }
        }
      });
    }

    this.time.delayedCall(8100, () => {
      // Stop GameScene if it's still running
      this.stopAllMusic();
      
      //BattleStateManager.reset();
      //this.scene.start('JoinScene');
    });
    
    // Return to join scene after delay (via BattlefieldLootScene)
    this.time.delayedCall(12000, () => {
      // Update state to 'battle-loot' before transitioning
      sessionStateManager.updateState('battle-loot');
      
      // Stop GameScene if it's still running
      this.stopGameScene();
      
      // Get battle ID from BattleStateManager
      // Also try to get it from GameScene's gameManager if available
      let battleId = BattleStateManager.getBattleId();
      
      // If BattleStateManager doesn't have it, try to get it from GameScene
      if (!battleId) {
        try {
          const gameScene = this.scene.get('GameScene');
          if (gameScene && gameScene.gameManager && gameScene.gameManager.currentBattleId) {
            battleId = gameScene.gameManager.currentBattleId;
            console.log('🏴‍☠️ Got battle ID from GameScene.gameManager:', battleId);
            // Update BattleStateManager with the real ID
            BattleStateManager.setBattleId(battleId);
          }
        } catch (error) {
          console.warn('⚠️ Could not access GameScene for battle ID:', error);
        }
      }
      
      // Transition to BattlefieldLootScene instead of directly to JoinScene
      if (battleId) {
        console.log('🏴‍☠️ Transitioning to BattlefieldLootScene with battleId:', battleId);
        this.scene.start('BattlefieldLootScene', {
          battleId
        });
      } else {
        console.warn('⚠️ No battle ID available, skipping BattlefieldLootScene');
        // Fallback to JoinScene if no battle ID
        BattleStateManager.reset();
        this.scene.start('JoinScene');
      }
    });
  }

  stopAllMusic() {
    try {
      // Stop rewards music
      if (this.currentRewardsMusic && this.currentRewardsMusicKey) {
        this.sound.stopByKey(this.currentRewardsMusicKey);
        console.log('🎵 Rewards music stopped:', this.currentRewardsMusicKey);
      }
      
      // Stop all other music that might be playing
      this.sound.stopAll();
      console.log('🎵 All music stopped');
      
      // Also stop any music from other scenes that might still be playing
      // This includes battle music, victory music, lobby music, etc.
      const allSounds = this.sound.sounds;
      for (const sound of allSounds) {
        if (sound && sound.isPlaying) {
          sound.stop();
          console.log('🎵 Stopped sound:', sound.key);
        }
      }
      
    } catch (error) {
      console.warn('Error stopping music:', error);
    }
  }

  stopGameScene() {
    try {
      // Check if GameScene is still running
      const gameScene = this.scene.get('GameScene');
      if (gameScene && gameScene.scene.isActive()) {
        console.log('🎮 Stopping GameScene');
        this.scene.stop('GameScene');
      }
      
      // Also check for any other battle-related scenes that might be running
      const scenesToStop = ['VictoryScene', 'WrapUpScene', 'PreBattleScene'];
      scenesToStop.forEach(sceneKey => {
        const scene = this.scene.get(sceneKey);
        if (scene && scene.scene.isActive()) {
          console.log(`🎮 Stopping ${sceneKey}`);
          this.scene.stop(sceneKey);
        }
      });
      
    } catch (error) {
      console.warn('Error stopping GameScene:', error);
    }
  }

  saveRewardsScript() {
    // Get JWT token from localStorage for authentication
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      console.warn('No JWT token available for saving rewards script');
      return;
    }

    // Save the rewards script to the database
    fetch('/api/battles/save-rewards-script', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        battleId: this.rewardsScript.battleId,
        scriptJson: this.rewardsScript,
        totalPlayers: this.eligiblePlayers.length,
        totalXpAwarded: this.eligiblePlayers.reduce((sum, p) => sum + p.rewards.xpEarned, 0),
        totalLootItems: this.eligiblePlayers.reduce((sum, p) => sum + (p.rewards.lootItems?.length || 0), 0)
      })
    }).catch(error => {
      console.error('Failed to save rewards script:', error);
    });
  }

  getRarityColor(rarity) {
    const colors = {
      'common': 0x808080,
      'uncommon': 0x00FF00,
      'rare': 0x0080FF,
      'epic': 0x8000FF,
      'legendary': 0xFF8000,
      'unique': 0xFF0000,
      'token': 0xF59E0B
    };
    return colors[rarity] || colors.common;
  }

  generateMockRewardsScript() {
    // Generate a mock rewards script for testing
    return {
      battleId: `battle_${Date.now()}`,
      timestamp: new Date().toISOString(),
      players: [
        {
          id: 'player_1',
          name: 'TestHero1',
          level: 5,
          isRealHero: true,
          heroId: 123,
          imageUrl: '/path/to/hero1.png',
          matchStats: {
            kills: 3,
            deaths: 1,
            damageDealt: 450,
            survivalTime: 120
          },
          rewards: {
            xpEarned: 150,
            leveledUp: true,
            newLevel: 6,
            lootItems: [
              {
                itemId: 'sword_001',
                itemName: 'Iron Sword',
                rarity: 'common',
                quantity: 1
              },
              {
                itemId: 'potion_002',
                itemName: 'Health Potion',
                rarity: 'uncommon',
                quantity: 2
              }
            ],
            specialFinds: []
          }
        },
        {
          id: 'player_2',
          name: 'TestHero2',
          level: 8,
          isRealHero: true,
          heroId: 456,
          imageUrl: '/path/to/hero2.png',
          matchStats: {
            kills: 5,
            deaths: 0,
            damageDealt: 720,
            survivalTime: 180
          },
          rewards: {
            xpEarned: 200,
            leveledUp: false,
            newLevel: 8,
            lootItems: [
              {
                itemId: 'armor_001',
                itemName: 'Steel Armor',
                rarity: 'rare',
                quantity: 1
              }
            ],
            specialFinds: [
              {
                type: 'unique',
                message: 'Unique Discovered!',
                itemId: 'unique_sword_001'
              }
            ]
          }
        }
      ],
      globalTiming: {
        playerTransitionDelay: 500,
        totalDuration: 45000
      }
    };
  }

  playRewardsMusic() {
    try {
      return;
    } catch (error) {
      console.warn('Could not play rewards music:', error);
    }
  }

  loadRewardsMusicFallback() {
    // Fallback: try to load from manifest if preloading failed
    if (this.rewardsMusicManifest && this.rewardsMusicManifest.length > 0) {
      const firstTrack = this.rewardsMusicManifest[0];
      const key = firstTrack.key;
      const theme = getCurrentTheme();
      const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
      const s3BasePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
      const musicPath = `${s3BasePath}/music/${key}`;
      
      console.log('🎵 Loading rewards music fallback:', musicPath);
      
      this.load.audio(key, musicPath);
      this.load.once('complete', () => {
        if (this.sound.get(key)) {
          const music = this.sound.play(key, { volume: 0.5, loop: true });
          this.currentRewardsMusic = music;
          this.currentRewardsMusicKey = key;
          console.log('🎵 Rewards music started (fallback):', firstTrack.title || key);
        }
      });
      this.load.start();
    } else {
      console.log('🎵 No rewards music available in theme');
    }
  }

  createProgressBar(width, height) {
    const barWidth = width * 0.8;
    const barHeight = 20;
    const barY = height - 40;
    const barX = width / 2;
    
    // Background bar (full width, dark)
    this.progressBarBg = this.add.rectangle(barX, barY, barWidth, barHeight, 0x1E293B);
    this.progressBarBg.setStrokeStyle(2, 0xFFFFFF);
    this.progressBarBg.setAlpha(0); // Start invisible
    this.progressBarBg.setDepth(20);
    
    // Progress bar fill (starts full width, shrinks as time passes)
    // Position at left edge of background bar
    const fillX = barX - barWidth / 2;
    this.progressBarFill = this.add.rectangle(fillX, barY, barWidth, barHeight - 4, 0xF3F300);
    this.progressBarFill.setOrigin(0, 0.5);
    this.progressBarFill.setAlpha(0); // Start invisible
    this.progressBarFill.setDepth(21);
    
    // Store initial values for update calculations
    this.progressBarInitialWidth = barWidth;
    this.progressBarInitialX = fillX;
    
    // Add glow effect to progress bar
    const progressGlow = this.add.rectangle(barX, barY, barWidth, barHeight + 4, 0xF3F300);
    progressGlow.setAlpha(0); // Start invisible
    progressGlow.setBlendMode(Phaser.BlendModes.ADD);
    progressGlow.setDepth(19);
    
    // Fade in progress bar after 1 second
    this.time.delayedCall(1000, () => {
      this.tweens.add({
        targets: [this.progressBarBg, this.progressBarFill, progressGlow],
        alpha: { from: 0, to: 1 },
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Set final alpha values
          this.progressBarBg.setAlpha(0.8);
          this.progressBarFill.setAlpha(0.9);
          progressGlow.setAlpha(0.2);
        }
      });
    });
    
    console.log('[RewardsScene] Progress bar created:', { barWidth, barX, fillX, barY });
  }

  update(time, delta) {
    // Initialize countdown start time on first update (when Phaser time is ready, after fade-in delay)
    if (this.countdownShouldStart && !this.countdownStartTime && time > this.countdownFadeInDelay) {
      this.countdownStartTime = time;
      this.countdownShouldStart = false;
      console.log('[RewardsScene] Countdown started:', {
        countdownStartTime: this.countdownStartTime,
        countdownDuration: this.countdownDuration,
        currentTime: time
      });
    }
    
    // Update progress bar if countdown has started
    if (this.countdownStartTime && this.progressBarFill && this.progressBarBg) {
      const elapsed = time - this.countdownStartTime;
      const remaining = Math.max(0, this.countdownDuration - elapsed);
      const progress = Math.max(0, Math.min(1, remaining / this.countdownDuration));
      const remainingSeconds = Math.ceil(remaining / 1000);
      
      // Use stored initial width or current background width
      const barWidth = this.progressBarInitialWidth || this.progressBarBg.width;
      const newWidth = barWidth * progress;
      this.progressBarFill.width = newWidth;
      
      // Position the fill bar correctly (left edge aligned with background bar's left edge)
      this.progressBarFill.x = this.progressBarInitialX || (this.progressBarBg.x - barWidth / 2);
      
      // Change color based on remaining time
      if (remainingSeconds <= 3) {
        this.progressBarFill.setFillStyle(0xEF4444); // Red
      } else if (remainingSeconds <= 6) {
        this.progressBarFill.setFillStyle(0xF59E0B); // Orange
      } else {
        this.progressBarFill.setFillStyle(0xF3F300); // Yellow
      }
      
      // Debug log every second
      if (!this.lastLogTime || time - this.lastLogTime >= 1000) {
        console.log('[RewardsScene] Progress bar update:', {
          time,
          elapsed,
          remaining,
          progress: progress.toFixed(2),
          remainingSeconds,
          newWidth: newWidth.toFixed(0)
        });
        this.lastLogTime = time;
      }
    }
  }

  shutdown() {
    // Stop rewards music
    try {
      if (this.currentRewardsMusic && this.currentRewardsMusicKey) {
        this.sound.stopByKey(this.currentRewardsMusicKey);
        console.log('🎵 Rewards music stopped:', this.currentRewardsMusicKey);
      }
    } catch (error) {
      console.warn('Could not stop rewards music:', error);
    }
    try { BattleStateManager.setRewardsScript(null); } catch (_) {}
    try { BattleStateManager.setBattleRewards(null); } catch (_) {}
    
    // Clear all state variables
    this.rewardsScript = null;
    this.currentPlayerIndex = 0;
    this.playerSprites = [];
    this.isPlaying = false;
    this.timeline = null;
    this.statsContainer = null;
    this.xpContainer = null;
    this.eligiblePlayers = [];
    this.rewardsMusicManifest = [];
    this.currentRewardsMusic = null;
    this.currentRewardsMusicKey = null;
    this.itemsById = null;
    this.countdownStartTime = null;
    
    console.log('🎁 RewardsScene shutdown');
  }
}
